import { ArrowLeft, QrCode, Loader2, AlertCircle, Wifi, RefreshCw, Power, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import useQRConnection from "@/hooks/useQRConnection";

export default function QRConnectionPanel({ authAxios, onClose, onConnected }) {
  const {
    qrDataUrl,
    status,
    loading,
    connectedOk,
    errorMsg,
    resetting,
    stuckReconnecting,
    leaseInfo,
    refreshQR,
    resetConnection,
    releaseLease,
  } = useQRConnection(authAxios, { onConnected, onClose });

  if (connectedOk) {
    return (
      <div className="absolute inset-0 bg-white z-50 flex flex-col items-center justify-center" data-testid="qr-connected-success">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
          <Wifi className="w-10 h-10 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-emerald-700 mb-2">WhatsApp Conectado!</h3>
        <p className="text-sm text-gray-500">Redirecionando...</p>
      </div>
    );
  }

  const isLeaseBlocked = leaseInfo?.blocked;

  return (
    <div className="absolute inset-0 bg-white z-50 flex flex-col" data-testid="qr-connection-panel">
      <div className="bg-emerald-600 text-white px-4 py-3 flex items-center gap-2">
        <button onClick={onClose}><ArrowLeft className="w-5 h-5" /></button>
        <h2 className="font-bold text-sm">Conectar WhatsApp</h2>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <QrCode className="w-12 h-12 text-emerald-600 mb-4" />
        <h3 className="text-lg font-bold mb-2">Escanear o QR Code</h3>
        <p className="text-sm text-gray-500 mb-2 max-w-sm">
          No seu celular: <strong>WhatsApp</strong> → <strong>Dispositivos conectados</strong> → <strong>Conectar dispositivo</strong>
        </p>
        <p className="text-xs text-gray-400 mb-6 max-w-sm">
          Aponte a camera do celular para o QR Code abaixo
        </p>

        {loading || resetting ? (
          <div className="w-52 h-52 bg-gray-50 border-2 border-dashed rounded-xl flex items-center justify-center flex-col gap-2">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
            <p className="text-xs text-gray-400">{resetting ? "Resetando sessão..." : "Gerando QR Code..."}</p>
          </div>
        ) : qrDataUrl ? (
          <div className="bg-white p-4 border-2 rounded-xl shadow-lg">
            <img src={qrDataUrl} alt="QR" className="w-48 h-48" data-testid="whatsapp-qr-image" />
          </div>
        ) : isLeaseBlocked ? (
          <div className="w-64 bg-orange-50 border-2 border-orange-300 rounded-xl flex items-center justify-center flex-col gap-2 px-4 py-6">
            <AlertCircle className="w-8 h-8 text-orange-500" />
            <p className="text-xs text-orange-800 font-semibold">Sessão bloqueada por outra instância</p>
            {leaseInfo.owner_instance_id && (
              <p className="text-[10px] text-orange-600 break-all font-mono">
                {leaseInfo.owner_instance_id}
              </p>
            )}
            <p className="text-[10px] text-orange-500 text-center mt-1">
              Isso acontece após um deploy. Aguarde ~1-2min ou clique em &ldquo;Liberar Lease&rdquo;.
            </p>
          </div>
        ) : status === "connection_failed" || (errorMsg && !isLeaseBlocked) ? (
          <div className="w-52 h-52 bg-red-50 border-2 border-dashed border-red-200 rounded-xl flex items-center justify-center flex-col gap-2 px-4">
            <AlertCircle className="w-8 h-8 text-red-300" />
            <p className="text-xs text-red-500 font-medium">QR não disponível</p>
            <p className="text-[10px] text-red-400">Clique em &ldquo;Resetar Sessão&rdquo; abaixo</p>
          </div>
        ) : stuckReconnecting ? (
          <div className="w-52 h-52 bg-amber-50 border-2 border-dashed border-amber-300 rounded-xl flex items-center justify-center flex-col gap-2 px-4">
            <AlertCircle className="w-8 h-8 text-amber-500" />
            <p className="text-xs text-amber-700 font-semibold">Sessão bloqueada</p>
            <p className="text-[10px] text-amber-600 text-center">Clique em &ldquo;Resetar Sessão&rdquo; para limpar e gerar novo QR</p>
          </div>
        ) : (
          <div className="w-52 h-52 bg-amber-50 border-2 border-dashed border-amber-200 rounded-xl flex items-center justify-center flex-col gap-2 px-4">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
            <p className="text-xs text-amber-600 font-medium">Aguardando QR Code...</p>
            <p className="text-[10px] text-amber-400">Isso pode levar alguns segundos</p>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-gray-500 my-4">
          {status === "waiting_qr" && !loading && qrDataUrl && (
            <><div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> Aguardando leitura do QR...</>
          )}
          {isLeaseBlocked && (
            <><AlertCircle className="w-3 h-3 text-orange-500" /> Aguardando liberação do lease...</>
          )}
          {status === "connecting" && !loading && !stuckReconnecting && !isLeaseBlocked && (
            <><Loader2 className="w-3 h-3 animate-spin" /> Conectando ao WhatsApp...</>
          )}
          {status === "reconnecting" && !stuckReconnecting && (
            <><Loader2 className="w-3 h-3 animate-spin" /> Reconectando...</>
          )}
          {stuckReconnecting && (
            <><AlertCircle className="w-3 h-3 text-amber-500" /> Sessão presa — clique em &ldquo;Resetar Sessão&rdquo;</>
          )}
          {(status === "connection_failed" || status === "error") && !isLeaseBlocked && (
            <><AlertCircle className="w-3 h-3 text-red-500" /> Falha na conexão. Clique em &ldquo;Resetar Sessão&rdquo;.</>
          )}
          {status === "qr_expired" && (
            <><AlertCircle className="w-3 h-3 text-amber-500" /> QR expirou. Clique em &ldquo;Resetar Sessão&rdquo; para tentar novamente.</>
          )}
        </div>

        {errorMsg && !isLeaseBlocked && (
          <p className="text-xs text-red-500 mb-3 max-w-sm bg-red-50 px-3 py-2 rounded-lg">{errorMsg}</p>
        )}

        <div className="flex gap-3 flex-wrap justify-center">
          <Button variant="outline" onClick={refreshQR} disabled={loading || resetting || stuckReconnecting || isLeaseBlocked} data-testid="refresh-qr-btn" className="gap-1">
            <RefreshCw className="w-4 h-4" /> Regenerar QR
          </Button>
          {isLeaseBlocked && (
            <Button
              variant="outline"
              onClick={releaseLease}
              disabled={loading}
              data-testid="release-lease-btn"
              className="gap-1 border-orange-300 text-orange-700 hover:bg-orange-50 ring-2 ring-orange-300"
            >
              <Unlock className="w-4 h-4" /> Liberar Lease
            </Button>
          )}
          <Button
            variant="outline"
            onClick={resetConnection}
            disabled={loading || resetting}
            data-testid="reset-connection-btn"
            className={`gap-1 border-red-200 text-red-600 hover:bg-red-50 ${stuckReconnecting ? "ring-2 ring-red-300" : ""}`}
          >
            <Power className="w-4 h-4" /> Resetar Sessão
          </Button>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>

        <div className="mt-6 bg-gray-50 rounded-xl p-4 max-w-sm text-left">
          <p className="text-xs font-bold text-gray-600 mb-2">Problemas com o QR Code?</p>
          <ul className="text-[11px] text-gray-500 space-y-1.5">
            {isLeaseBlocked ? (
              <>
                <li>1. Clique em <strong>&ldquo;Liberar Lease&rdquo;</strong> para forçar a liberação sem apagar a sessão</li>
                <li>2. Se o problema persistir após ~2min, clique em <strong>&ldquo;Resetar Sessão&rdquo;</strong></li>
                <li>3. O lease é liberado automaticamente ~90s após o último deploy</li>
              </>
            ) : (
              <>
                <li>1. Clique em <strong>&ldquo;Resetar Sessão&rdquo;</strong> para limpar a sessão antiga</li>
                <li>2. Aguarde o novo QR Code aparecer</li>
                <li>3. Escaneie com o WhatsApp do seu celular</li>
                <li>4. Após conectar, o QR desaparece automaticamente</li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
